import React, { useCallback, useEffect, useState } from 'react';
import { organisationsAPI } from '../../../organisations/api/organisationsAPI';
import { isForbidden, organisationErrorMessage } from '../../../organisations/lib/errorMessage';
import { leavingLabel } from '../../../organisations/lib/schoolYears';
import { compareCustomGroups } from '../../lib/scopes';
import { teacherAnalyticsAPI } from '../../api/teacherAnalyticsAPI';
import { visibleSections } from './profileSections';
import EmptyState from '../common/EmptyState';
import { orDash } from '../../lib/format';
import { initialActivityPeriod } from './activity/lib/activityFormat';
import { writingRowId } from './activity/WritingSection';
import { caseAnswerAnchor } from './activity/CasesSection';
import PaperReview from './activity/PaperReview';
import QuizReview from './activity/QuizReview';

// Chip order: Oxbridge, admissions test, career, any other custom group,
// then the form, then subject classes. The server sends groups by name.
const chipKind = (g) => {
  if (g.group_type !== 'class') return 'group';
  return g.subject == null ? 'form' : 'class';
};

/** Header: who the student is, when they leave, their form, classes and groups. */
const ProfileHeader = ({ student }) => {
  const ofKind = (kind) => student.groups.filter((g) => chipKind(g) === kind);
  const chips = [
    ...ofKind('group').sort(compareCustomGroups),
    ...ofKind('form'),
    ...ofKind('class'),
  ];
  return (
    <div className="td-profile__identity">
      <div>
        <h2 className="td-profile__name">{orDash(student.name)}</h2>
        <span className="td-profile__email">{orDash(student.email)}</span>
      </div>
      <div className="td-chips">
        {student.cohort_year != null ? (
          <span className="td-chip">{leavingLabel(student.cohort_year, student.year_group)}</span>
        ) : null}
        {chips.map((g) => (
          <span key={g.id} className={`td-chip td-chip--${chipKind(g)}`}>{g.name}</span>
        ))}
      </div>
    </div>
  );
};

/**
 * One student's profile. The header and which tabs this viewer sees come
 * from GET /api/organisations/students/<id>; the Activity tab loads its own
 * sections for the profile's period; Documents and Feedback come from the
 * tracker endpoints, unchanged. A 403 is shown as "no access", not as a
 * failure. The tabs come from ./profileSections.
 *
 * With `paper` (the dashboard's ?paper=, see activity/lib/paperReview) the
 * profile shows that paper's review instead of its tabs; `onOpenPaper(ref)`
 * opens one (null closes it) and the review's Back returns to Activity.
 * `quiz` (?quiz=, a Quiz generator row's session_id) and `onOpenQuiz` do
 * the same for a custom quiz's review. A paper, if both are set, wins.
 *
 * The open tab is the dashboard's ?ptab= and the Activity section last
 * moved to its ?section=; `onNavigate(patch, { push })` writes them (the
 * dashboard's setParams), in one call per event. An unknown tab, or one
 * this viewer does not see, opens the first. The dashboard drops both
 * when the student changes, so opening another student starts on the
 * first tab and a panel that fetches on open never fetches for a student
 * whose tab the viewer did not open.
 *
 * The Activity period lives here, so it survives switching tabs. It starts
 * from the dashboard's period (`today` has no Activity equivalent and opens
 * on 7 days) and follows it when the dashboard's period changes.
 */
const StudentProfile = ({
  studentId, period, currentUserId, onBack, paper = null, onOpenPaper = null, paperHref,
  quiz = null, onOpenQuiz = null, quizHref, ptab = null, section = null, onNavigate,
}) => {
  // A tab click pushes, so Back returns to the tab before. Its section is
  // the Activity tab's own, so another tab starts at the top.
  const selectTab = (value) => onNavigate({ ptab: value, section: null }, { push: true });

  const [activityPeriod, setActivityPeriod] = useState(() => initialActivityPeriod(period));
  useEffect(() => { setActivityPeriod(initialActivityPeriod(period)); }, [period]);

  const [profile, setProfile] = useState(null);
  const [profileError, setProfileError] = useState('');

  const [tracker, setTracker] = useState(null);
  const [trackerLoading, setTrackerLoading] = useState(false);
  const [trackerForbidden, setTrackerForbidden] = useState(false);
  const [trackerError, setTrackerError] = useState('');
  const [posting, setPosting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setProfile(null);
    setProfileError('');
    organisationsAPI.getStudentProfile(studentId)
      .then((data) => { if (!cancelled) setProfile(data); })
      .catch((err) => {
        if (cancelled) return;
        setProfileError(isForbidden(err)
          ? 'You do not have access to this student.'
          : organisationErrorMessage(err, 'Could not load this student. Please try again.'));
      });
    return () => { cancelled = true; };
  }, [studentId]);

  // Tracker documents and comments. A 403 here is expected whenever the
  // viewer may not see the student, and must surface as a state.
  const loadTracker = useCallback(async () => {
    setTrackerLoading(true);
    setTrackerForbidden(false);
    setTrackerError('');
    try {
      setTracker(await teacherAnalyticsAPI.getStudentTracker(studentId));
    } catch (err) {
      if (isForbidden(err)) setTrackerForbidden(true);
      else setTrackerError('Could not load this student’s documents and feedback.');
      setTracker(null);
    } finally {
      setTrackerLoading(false);
    }
  }, [studentId]);

  useEffect(() => { loadTracker(); }, [loadTracker]);

  // With `subject` ({ subject_kind, subject_id }) the comment is on one piece
  // of writing; without it, on the student as a whole.
  const postComment = useCallback(async (commentText, subject = null) => {
    setPosting(true);
    try {
      const res = await teacherAnalyticsAPI.postStudentComment(studentId, commentText, subject);
      // The POST returns { id, created_at, subject_kind, subject_id } but not
      // the text, so the row is built from what was sent.
      setTracker((prev) => ({
        ...(prev || {}),
        comments: [
          {
            id: res?.id || `local-${Date.now()}`,
            comment_text: commentText,
            created_at: res?.created_at || new Date().toISOString(),
            teacher_user_id: currentUserId,
            subject_kind: subject ? subject.subject_kind : null,
            subject_id: subject ? subject.subject_id : null,
          },
          ...((prev && prev.comments) || []),
        ],
      }));
      return true;
    } catch {
      setTrackerError('Could not post feedback. Please try again.');
      return false;
    } finally {
      setPosting(false);
    }
  }, [studentId, currentUserId]);

  // A comment posted on an essay or a case answer in the Activity tab also
  // belongs in the Feedback tab's list, with the piece it is about.
  const addPieceComment = useCallback((item, comment) => {
    setTracker((prev) => (prev ? {
      ...prev,
      comments: [
        {
          id: comment.id,
          comment_text: comment.text,
          created_at: comment.created_at,
          teacher_user_id: currentUserId,
          subject_kind: item.kind,
          subject_id: item.id,
        },
        ...(prev.comments || []),
      ],
    } : prev));
  }, [currentUserId]);

  // "On: TARA essay" in the Feedback tab: the Activity tab opens, then the
  // piece's anchor goes in the hash (after the navigation, which would drop
  // it). An essay is a Writing row, which WritingSection opens and scrolls
  // to when its list arrives; a case answer is in Cases, where CasesSection
  // opens that answer on its case's card. A piece beyond its list's first
  // page (or outside the period) is not opened. `close` is the rest of the
  // same navigation (closing a paper review), and `push` whether it adds
  // an entry, as a tab click does.
  const openPiece = useCallback(({ kind, id }, { close = {}, push = true } = {}) => {
    onNavigate({ ...close, ptab: 'activity', section: null }, { push });
    window.location.hash = kind === 'case_study' ? caseAnswerAnchor(id) : writingRowId({ kind, id });
  }, [onNavigate]);

  // Back from a paper review, or out of it to an essay's Writing piece:
  // close the review and land on the Activity tab, replacing the entry as
  // closing a review always has.
  const closePaper = useCallback(() => {
    onNavigate({ paper: null, ptab: 'activity' });
  }, [onNavigate]);
  const openPieceFromPaper = useCallback((piece) => {
    openPiece(piece, { close: { paper: null }, push: false });
  }, [openPiece]);
  // Back from a quiz review, the same way.
  const closeQuiz = useCallback(() => {
    onNavigate({ quiz: null, ptab: 'activity' });
  }, [onNavigate]);
  const review = paper ? 'paper' : (quiz ? 'quiz' : null);

  const tabs = visibleSections(profile);
  const open = tabs.find((t) => t.value === ptab) || tabs[0];
  const panel = open.value;
  const ActivePanel = open.Panel;

  return (
    <div className="td-profile">
      {profile && review ? null : (
        <button type="button" className="td-btn td-btn--ghost td-profile__back" onClick={onBack}>
          ← Back to students
        </button>
      )}

      {profileError ? <EmptyState title={profileError} /> : null}
      {!profile && !profileError ? <p className="td-loading">Loading…</p> : null}

      {profile && review === 'paper' ? (
        <PaperReview
          key={studentId}
          studentId={studentId}
          studentName={profile.student.name}
          paperRef={paper}
          onOpenPaper={onOpenPaper}
          onBack={closePaper}
          onOpenPiece={openPieceFromPaper}
        />
      ) : null}

      {profile && review === 'quiz' ? (
        <QuizReview
          key={studentId}
          studentId={studentId}
          studentName={profile.student.name}
          sessionId={quiz}
          onBack={closeQuiz}
        />
      ) : null}

      {profile && !review ? (
        <>
          <ProfileHeader student={profile.student} />

          <div className="td-subtabs" role="tablist">
            {tabs.map((p) => (
              <button
                key={p.value}
                type="button"
                role="tab"
                aria-selected={panel === p.value}
                className={`td-subtab${panel === p.value ? ' td-subtab--active' : ''}`}
                onClick={() => selectTab(p.value)}
              >
                {p.label}
              </button>
            ))}
          </div>

          <ActivePanel
            studentId={studentId}
            currentUserId={currentUserId}
            profile={profile}
            period={activityPeriod}
            onPeriodChange={setActivityPeriod}
            onCommentPosted={addPieceComment}
            onOpenPiece={openPiece}
            onOpenPaper={onOpenPaper}
            paperHref={paperHref}
            onOpenQuiz={onOpenQuiz}
            quizHref={quizHref}
            section={section}
            onSectionChange={(key) => onNavigate({ section: key })}
            tracker={{
              data: tracker,
              loading: trackerLoading,
              forbidden: trackerForbidden,
              error: trackerError,
              posting,
              onPostComment: postComment,
            }}
          />
        </>
      ) : null}
    </div>
  );
};

export default StudentProfile;
